/* eslint-disable react-hooks/rules-of-hooks */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil'
import styled from '@emotion/styled'
import { Divider, IconButton, Typography } from '@mui/material'
import { InfoCircle } from '@emotion-icons/fa-solid/InfoCircle'
import { Edit } from '@emotion-icons/material/Edit'
import { ZoomIn } from '@emotion-icons/material/ZoomIn'
import { ZoomOut } from '@emotion-icons/material/ZoomOut'
import { capitalCase } from 'capital-case'
import { FormControlLabel as DefaultFormControlLabel } from 'src/components/checkbox'
import { Panel } from 'src/components/sidebar/panel'
import { Colors, Shapes, printStyle } from 'src/components/theme'
import { saveHTMLToData } from 'src/utils/html-to-image'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { InsightDownloadPreview } from 'src/views/insight-download/preview'
import { AlphamapLogoThrobber } from 'src/components/alphamap-logo/throbber'
import { AntSwitch } from 'src/components/switch'
import { getLockedInsightWidgets, getPrintableInsightWidgets } from 'src/views/insight-download/utils'
import { PaddedBox } from 'src/components/generic/padded-box'
import { ColorPickerInput } from 'src/components/color-picker-input'
import { TextField } from 'src/components/text-field'
import { Dropdown, DropdownOption, DropdownOptionAction } from 'src/components/dropdown'
import { useHotkey } from 'src/hooks/use-hotkey'
import { InsightReportOrientation, InsightReportOrientations, ReportTheme } from 'src/atoms/report-insight'
import { PAGE_SIZES } from 'src/constants/report-insights'
import { InsightDownloadPDF } from 'src/views/insight-download/pdf'
import { InsightWidget } from 'src/types/report-insights'
import { InsightDownloadLoading } from 'src/views/insight-download/modal/loading'
import { UploadField } from 'src/components/upload-field'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { SectionsPanel } from 'src/views/insight-download/modal/widgets-section'
import { logEvent } from 'src/components/analytics'
import { reportThemesClient } from 'src/clients'
import { growlError } from 'src/components/growl'

import * as reportInsight from 'src/atoms/report-insight'
import { useTier } from 'src/hooks/use-tier'
import { LockedFeature } from 'src/components/locked-feature'
import { HelpTooltip } from 'src/components/help-tooltip'

const Wrapper = styled.div`
  display: flex;
  height: calc(100% - 40px);
  background-color: ${Colors.bgWhite};
`

const ConfigColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 260px;
  height: 100%;
  overflow-y: auto;
  ${FANCY_SCROLLBAR}
  ${printStyle`
    display: none;
  `}
`

const RightConfigColumn = styled(ConfigColumn)`
  padding-top: ${Shapes.spacing[4]}px;
  min-height: 100%;
  max-height: 100%;
  overflow: hidden;
  /* allow overflow-y: on second child div */
  > div:nth-of-type(2) {
    overflow-y: scroll;
    ${FANCY_SCROLLBAR}
  }
`

const PagesColumn = styled.div`
  position: relative;
  flex: 1 1 0%;
  overflow-y: auto;
  ${FANCY_SCROLLBAR}
  border-right: 1px solid ${Colors.border};
  border-left: 1px solid ${Colors.border};
  background-color: ${Colors.bgDark};
`

const PageColumnHeader = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: ${Shapes.spacing[4]}px;
  z-index: 1;
`

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[2]}px;
`

const Pages = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[8]}px;
  margin-inline: auto;
  width: max-content;
  padding-bottom: ${Shapes.spacing[8]}px;
`

export const PanelFormControlLabel = styled(DefaultFormControlLabel)`
  color: ${Colors.textDark};
  margin: 0;
  .MuiFormControlLabel-label {
    margin-left: ${Shapes.spacing[2]}px;
  }
  .MuiCheckbox-root {
    padding: 0;
  }

  &:hover {
    .MuiFormControlLabel-label {
      color: ${Colors.textBlack};
    }
  }
  p {
    display: flex;
    align-items: center;
    gap: ${Shapes.spacing[1]}px;
  }
`

export const PanelContents = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Shapes.spacing[0]}px ${Shapes.spacing[4]}px;
  gap: ${Shapes.spacing[1]}px;
`

const ThrobberContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  & img {
    position: absolute;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
  }
`

type ThrobberProps = {
  zoom?: number
}
const Throbber = (props: ThrobberProps) => {
  const { zoom } = props

  return (
    <ThrobberContainer
      style={{
        position: 'absolute',
        top: `50%`,
        left: `50%`,
        transform: `scale(${zoom || 1}) translate(-50%, -50%)`,
      }}>
      <AlphamapLogoThrobber />
    </ThrobberContainer>
  )
}

export type PanelProps = {
  loading: boolean
}

const SettingsPanel = (props: PanelProps) => {
  const { loading } = props
  const [pageSize, setPageSize] = useRecoilState(reportInsight.reportPageSize)
  const [reportOrientation, setReportOrientation] = useRecoilState(reportInsight.reportOrientation)

  return (
    <>
      <Panel title="Settings">
        <PanelContents>
          <Dropdown
            label="Page Size"
            disabled={loading}
            style={{ width: '100%' }}
            value={pageSize}
            options={Object.keys(PAGE_SIZES).map((size) => ({
              id: size,
              label: capitalCase(size),
            }))}
            onChangeOption={(option) => {
              logEvent('insight-download-config', { action: 'page-size', value: option?.id })
              setPageSize((option?.id as keyof typeof PAGE_SIZES) || PAGE_SIZES.letter)
            }}
          />
          <Dropdown
            label="Orientation"
            disabled={loading}
            style={{ width: '100%' }}
            value={reportOrientation}
            options={Object.keys(InsightReportOrientations).map((orientation) => ({
              id: InsightReportOrientations[orientation as keyof typeof InsightReportOrientations],
              label: orientation,
            }))}
            onChangeOption={(option) => {
              logEvent('insight-download-config', { action: 'orientation', value: option?.id })
              setReportOrientation((option?.id as InsightReportOrientation) || InsightReportOrientations.Portrait)
            }}
          />
          <PaddedBox padding={Shapes.spacing[1]} />
        </PanelContents>
      </Panel>
    </>
  )
}

const BrandingSelectContainer = styled.div`
  position: relative;
`

const LockedFeatureContainer = styled.div`
  position: absolute;
  right: ${Shapes.spacing[7]}px;
  top: 50%;
  transform: translateY(-50%);
`

const SaveButton = styled(ButtonWithIcon)`
  width: 100%;
  padding-block: ${Shapes.spacing[1]}px;
`

const DeleteButton = styled(ButtonWithIcon)`
  width: 100%;
  padding-block: ${Shapes.spacing[1]}px;
`

const ThemePanel = (props: PanelProps) => {
  const { loading: loadingFromProps } = props
  const [coverPage, setCoverPage] = useRecoilState(reportInsight.reportCoverPage)
  const resetCoverPage = useResetRecoilState(reportInsight.reportCoverPage)
  const [reportHeader, setReportHeader] = useRecoilState(reportInsight.reportHeader)
  const [reportFooter, setReportFooter] = useRecoilState(reportInsight.reportFooter)
  const [theme, setTheme] = useRecoilState(reportInsight.theme)
  const [themes, setThemes] = useRecoilState(reportInsight.themes)
  const [editTheme, setEditTheme] = useState<Partial<ReportTheme> | null>(null)
  const [loading, setLoading] = useState<boolean>(loadingFromProps)
  const [error, setError] = useState<boolean>(false)
  const locked = !useTier('intermediate')

  const toggleCoverPage = useCallback(() => {
    logEvent('insight-download-config', { action: 'cover-page', value: !coverPage })
    if (!coverPage) {
      resetCoverPage()
      return
    }
    setCoverPage(undefined)
  }, [coverPage])
  const toggleReportHeader = useCallback(() => {
    logEvent('insight-download-config', { action: 'header' })
    setReportHeader((header) => ({
      ...header,
      visible: !header.visible,
    }))
  }, [])

  const toggleReportFooter = useCallback(() => {
    logEvent('insight-download-config', { action: 'footer' })
    setReportFooter((footer) => ({
      ...footer,
      visible: !footer.visible,
    }))
  }, [])

  const handleThemeConfigChange = (field: keyof ReportTheme, value?: string | null) => {
    if (field === 'id') {
      return
    }
    setEditTheme((theme) => ({ ...(theme as ReportTheme), [field]: value }))
  }

  const handleThemeChange = (option: DropdownOption | null) => {
    if (locked) {
      return
    }
    if (option?.id === themes['custom'].id) {
      setEditTheme({
        id: 'custom',
        primaryColor: themes.custom.primaryColor,
        backgroundColor: themes.custom.backgroundColor,
      })
    } else {
      setEditTheme(null)
    }
    setTheme(option?.id || themes.alphamap.id)
  }

  const handleEditTheme = (option: DropdownOption | null) => {
    if (option) {
      setEditTheme(themes[option.id])
    }
  }

  const saveTheme = () => {
    const id = editTheme?.id
    const theme: ReportTheme = { ...(editTheme as ReportTheme) }
    if (!theme.name || !theme.primaryColor || !theme.backgroundColor) {
      setError(true)
      return
    }
    setError(false)
    setLoading(true)
    if (!id || id === 'custom') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: deleted, ...themeToCreate } = theme
      reportThemesClient
        .createReportTheme(themeToCreate)
        .then((theme) => {
          const { custom, ...rest } = themes
          const newThemes: Record<string, ReportTheme> = { ...rest, [theme.id]: theme, custom }
          setEditTheme(null)
          setThemes(newThemes)
          setTheme(theme.id)
        })
        .catch(growlError)
        .finally(() => setLoading(false))
    } else {
      const existingTheme = { ...theme, id }
      reportThemesClient
        .updateReportTheme(existingTheme)
        .then((theme) => {
          setEditTheme(null)
          setThemes((themes) => {
            const newThemes = { ...themes }
            newThemes[theme.id] = theme
            return newThemes
          })
          setTheme(theme.id)
        })
        .catch(growlError)
        .finally(() => setLoading(false))
    }
  }

  const deleteTheme = () => {
    if (
      (editTheme && editTheme.id == undefined) ||
      (editTheme && (editTheme.id === 'alphamap' || editTheme.id == 'custom'))
    ) {
      return
    }
    setLoading(true)
    if (editTheme?.id) {
      reportThemesClient
        .deleteReportTheme({ id: editTheme.id })
        .then(() => {
          const { [editTheme.id as string]: deleted, ...newThemes } = themes
          setEditTheme(null)
          setThemes(newThemes)
          setTheme('alphamap')
          return deleted
        })
        .catch(growlError)
        .finally(() => setLoading(false))
    }
  }

  const dropdownAction: DropdownOptionAction = useMemo(() => {
    if (locked) {
      return {
        icon: (
          <HelpTooltip file="locked-feature" placement="right" immediate>
            <LockedFeature isLocked={locked} />
          </HelpTooltip>
        ),
      }
    }
    return {
      icon: <Edit size={16} />,
      label: 'Edit',
      onClick: handleEditTheme,
    }
  }, [locked])

  useEffect(() => {
    reportThemesClient
      .getReportThemes()
      .then((themes) =>
        setThemes((prev) => {
          const themesObj: Record<string, ReportTheme> = {}
          themes.forEach((theme) => (themesObj[theme.id] = theme))
          const { custom, ...defaultThemes } = prev
          return { ...defaultThemes, ...themesObj, custom }
        })
      )
      .catch(growlError)

    // Reset theme to alphapmap if theme is a custom unsaved theme or branding is locked
    return () => {
      setTheme((theme) => {
        if (theme === 'custom' || locked) {
          return 'alphamap'
        }
        return theme
      })
    }
  }, [])

  useEffect(() => {
    setLoading(loadingFromProps)
  }, [loadingFromProps])

  return (
    <>
      <Panel title="Theme">
        <PanelContents>
          <PanelFormControlLabel
            disabled={props.loading}
            control={<AntSwitch checked={!!coverPage} onChange={toggleCoverPage} />}
            label="Cover Page"
          />
          <PanelFormControlLabel
            disabled={props.loading}
            control={<AntSwitch checked={reportHeader.visible} onChange={toggleReportHeader} />}
            label="Page headers"
          />
          <PanelFormControlLabel
            disabled={props.loading}
            control={<AntSwitch checked={reportFooter.visible} onChange={toggleReportFooter} />}
            label="Page footers"
          />
          <PaddedBox padding={Shapes.spacing[1]} />
          <BrandingSelectContainer>
            <Dropdown
              label="Branding"
              disabled={loading}
              style={{ width: '100%' }}
              value={theme}
              options={Object.keys(themes).map((themeId) => ({
                id: themeId,
                label: themes[themeId].name,
                showAction: (locked && themeId !== 'alphamap') || (themeId !== 'custom' && themeId !== 'alphamap'),
              }))}
              optionsAction={dropdownAction}
              persistentAction={locked}
              onChangeOption={handleThemeChange}
            />
            <LockedFeatureContainer>
              <HelpTooltip file="locked-feature" placement="right" immediate>
                <LockedFeature tier="intermediate" />
              </HelpTooltip>
            </LockedFeatureContainer>
          </BrandingSelectContainer>
          {editTheme && (
            <>
              <PaddedBox padding={Shapes.spacing[1]} />
              <TextField
                autoFocus
                label="Branding Name"
                disabled={loading}
                value={editTheme.name}
                error={error && !editTheme.name}
                placeholder="Enter name to save brand"
                style={{ width: '100%' }}
                onChange={(e) => handleThemeConfigChange('name', e.target.value)}
              />
              <PaddedBox padding={Shapes.spacing[1]} />
              <UploadField
                label="Branding Logo"
                disabled={loading}
                value={editTheme.logo}
                placeholder="Upload logo"
                onChangeDataUri={(data) => handleThemeConfigChange('logo', data)}
                style={{ width: '100%' }}
              />
              <PaddedBox padding={Shapes.spacing[1]} />
              <ColorPickerInput
                label="Primary Color"
                disabled={loading}
                value={editTheme.primaryColor}
                error={error && !editTheme.primaryColor}
                onChange={(color) => handleThemeConfigChange('primaryColor', color)}
              />
              <PaddedBox padding={Shapes.spacing[1]} />
              <ColorPickerInput
                label="Background Color"
                disabled={loading}
                value={editTheme.backgroundColor}
                error={error && !editTheme.backgroundColor}
                onChange={(color) => handleThemeConfigChange('backgroundColor', color)}
              />
              <PaddedBox padding={Shapes.spacing[1]} />
              <SaveButton onClick={saveTheme} disabled={loading}>
                Save
              </SaveButton>
              {editTheme.id !== 'custom' && editTheme.id !== 'alphamap' && (
                <DeleteButton ghost color={Colors.redDark} onClick={deleteTheme} disabled={loading}>
                  Delete theme
                </DeleteButton>
              )}
            </>
          )}
        </PanelContents>
      </Panel>
    </>
  )
}

type PagesPanelProps = PanelProps & {
  onScrollToPage: (page: number) => void
}

const PagesPanel = (props: PagesPanelProps) => {
  const { loading, onScrollToPage } = props
  const zoom = 0.2
  return (
    <Panel
      style={{
        marginTop: `${Shapes.spacing[4]}px`,
        position: 'relative',
        height: '100%',
        borderTop: `1px solid ${Colors.border}`,
      }}
      title="Preview">
      {loading ? (
        <Throbber />
      ) : (
        <PanelContents
          style={{
            position: 'absolute',
            left: `50%`,
            transform: `scale(${zoom}) translateX(-50%)`,
            transformOrigin: '0 0',
          }}>
          <Pages>
            <InsightDownloadPreview pageNumberZoom={1 / zoom} onClickPagePreview={onScrollToPage} />
          </Pages>
        </PanelContents>
      )}
    </Panel>
  )
}

const FloatingAlertContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[3]}px;
  margin: 0 ${Shapes.spacing[4]}px;
  margin-top: ${Shapes.spacing[3]}px;
  padding: ${Shapes.spacing[3]}px;
  background-color: ${Colors.bgWhite};
  border-radius: ${Shapes.borderRadius}px;
  border: solid 2px ${Colors.blueDark};
  box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.1);
  z-index: 1;
`

const InfoWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[3]}px;
`

const ZoomButton = styled(IconButton)`
  border-radius: ${Shapes.borderRadius}px;
  background-color: ${Colors.blueDark};
  color: ${Colors.textWhite};
  padding: ${Shapes.spacing[1]}px;
  height: ${Shapes.spacing[6]}px;
  width: ${Shapes.spacing[6]}px;
  &:hover {
    box-shadow: ${Colors.BOX_SHADOW};
    background-color: ${Colors.blueDark};
  }
  &:disabled {
    background-color: ${Colors.bgWhite};
    color: ${Colors.textDisabled};
  }
`

const ZOOM_FACTOR = 0.1
const MIN_ZOOM = 0.5
const MAX_ZOOM = 1.5

export const InsightDownloadModalContent = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const setWidgets = useSetRecoilState(reportInsight.widgets)
  const setLockedWidgets = useSetRecoilState(reportInsight.lockedWidgets)
  const setMapStatic = useSetRecoilState(reportInsight.isMapStatic)
  const [loading, setLoading] = useRecoilState(reportInsight.loading)
  const [cssZoom, setCssZoom] = useState(0.8)
  const [totalWidgetsCount, setTotalWidgetCount] = useState(0)
  const [completedCount, setCompletedCount] = useState(0)

  // Get all printable widgets available on page
  const printableWidgets = useMemo(() => getPrintableInsightWidgets(), [])
  const lockedWidgets = useMemo(() => getLockedInsightWidgets(), [])

  // Get percentage complete
  const percentageComplete = useMemo(() => {
    if (!totalWidgetsCount) {
      return 0
    }
    return Math.round((completedCount / totalWidgetsCount) * 100)
  }, [completedCount, totalWidgetsCount])

  const scrollToPage = useCallback(
    (index: number) => {
      if (!containerRef.current) {
        return
      }
      const page = containerRef.current.children.item(index)
      if (!page) {
        return
      }
      page.scrollIntoView({ behavior: 'smooth' })
    },
    [containerRef]
  )

  // Handle zooming in/out
  // Buttons
  const onZoomIn = () => {
    setCssZoom((zoom) => {
      if (zoom + ZOOM_FACTOR >= MAX_ZOOM) {
        return MAX_ZOOM
      }
      return zoom + ZOOM_FACTOR
    })
  }
  const onZoomOut = () => {
    setCssZoom((zoom) => {
      if (zoom - ZOOM_FACTOR <= MIN_ZOOM) {
        return MIN_ZOOM
      }
      return zoom - ZOOM_FACTOR
    })
  }
  // Hotkeys
  useHotkey('ctrl+-', () => onZoomOut())
  useHotkey('ctrl+=', () => onZoomIn())
  // CMD + Scroll
  useEffect(() => {
    const onScroll = (e: WheelEvent) => {
      if (e.metaKey) {
        e.preventDefault()
        if (e.deltaY > 0) {
          onZoomOut()
        } else {
          onZoomIn()
        }
        return false
      }
    }
    document.addEventListener('wheel', onScroll)
    return () => {
      document.removeEventListener('wheel', onScroll)
    }
  }, [])

  // Fetch widgets images
  useEffect(() => {
    if (!printableWidgets.length) {
      setTotalWidgetCount(0)
      setCompletedCount(0)
      return
    }
    setLoading(true)
    // Freeze maps
    setMapStatic(true)
    // Set total widgets count
    setTotalWidgetCount(printableWidgets.length)

    // Save all widgets as image data
    const func = async () => {
      // loop through individually to show progress
      const widgets = []
      for (const printable of printableWidgets) {
        const image = await saveHTMLToData(printable.id, 2.5, 'png')
        widgets.push({
          ...printable,
          image,
        })
        setCompletedCount((count) => count + 1)
      }

      // Filter out widgets without images
      const widgetsWithImages = widgets.filter((widget) => !!widget.image) as InsightWidget[]
      setWidgets(widgetsWithImages)
      setLockedWidgets(lockedWidgets)
    }

    void func().finally(() => {
      setLoading(false)
      // Unfreeze maps
      setMapStatic(false)
    })
  }, [])

  return (
    <Wrapper>
      <ConfigColumn>
        <SettingsPanel loading={loading} />
        <Divider variant="fullWidth" />
        <ThemePanel loading={loading} />
        <Divider variant="fullWidth" />
        <SectionsPanel loading={loading} />
        <Divider variant="fullWidth" />
      </ConfigColumn>
      <PagesColumn>
        <PageColumnHeader>
          {lockedWidgets.length > 0 && (
            <FloatingAlertContainer>
              <InfoWrapper>
                <InfoCircle color={Colors.blueDark} size={Shapes.spacing[4]} />
                <Typography variant="text1m">
                  The following <b>widgets won't be displayed</b> due to not having the right AlphaMap plan:
                  <br />
                  {lockedWidgets.map((widget) => widget.label).join(', ')}
                </Typography>
              </InfoWrapper>
              <ButtonWithIcon inverted>Upgrade Plan</ButtonWithIcon>
            </FloatingAlertContainer>
          )}
          <ActionsContainer>
            <ZoomButton onClick={onZoomOut} disabled={cssZoom <= MIN_ZOOM}>
              <ZoomOut size={20} />
            </ZoomButton>
            <ZoomButton onClick={onZoomIn} disabled={cssZoom >= MAX_ZOOM}>
              <ZoomIn size={20} />
            </ZoomButton>
          </ActionsContainer>
        </PageColumnHeader>
        {percentageComplete <= 100 && loading ? (
          <InsightDownloadLoading percentage={percentageComplete} />
        ) : (
          <Pages
            ref={containerRef}
            style={{
              transform: `scale(${cssZoom})`,
              transformOrigin: 'center 0',
            }}>
            <InsightDownloadPreview />
          </Pages>
        )}
      </PagesColumn>
      <RightConfigColumn>
        <InsightDownloadPDF disabled={loading} />
        <PagesPanel loading={loading} onScrollToPage={scrollToPage} />
      </RightConfigColumn>
    </Wrapper>
  )
}
